import { Robot } from '@/entities/Robot';
import { PlayerBase } from '@/entities/GameBase';
import { ComponentType, ComponentCategory, getEvolutionMultiplier } from '@/components/ComponentDefs';
import { MAP_DECORATIONS } from '@/map/MapData';
import { TILE_SIZE, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { EventBus, GameEvents } from '@/events/EventBus';

// ── Support beam VFX: sinusoidal beam + particles ──
// Throttle: max 1 beam per robot pair per 200ms
const beamThrottle = new Map<string, number>();
let beamThrottleCleanupCounter = 0;

/** Clear all cached state for support module */
export function resetSupportCaches(): void {
  beamThrottle.clear();
  beamThrottleCleanupCounter = 0;
}

// Find nearest harvestable decoration within sensor range (for excavator auto-seek)
// If robot has no sensor but has an excavator, use excavator supportRange × 3 as fallback detection range
export function findNearestHarvestable(robot: Robot): { x: number; y: number } | null {
  let range = robot.sensorRange;
  if (range <= 0) {
    // Fallback: excavator can "sense" nearby resources even without a radar
    const exc = robot.components.find(c => !c.broken && c.def.type === ComponentType.EXCAVATOR);
    if (exc) {
      const mult = getEvolutionMultiplier(exc.evoLevel);
      range = exc.def.supportRange * mult * 3; // 3× harvest range as detection range
    }
  }
  const rangeSq = range * range;
  if (rangeSq <= 0) return null;
  let bestX = 0, bestY = 0;
  let bestDistSq = rangeSq;
  let found = false;
  for (let i = 0; i < MAP_DECORATIONS.length; i++) {
    const d = MAP_DECORATIONS[i];
    if (!DECOR_RESOURCE_YIELDS[d.type]) continue;
    const wx = d.col * TILE_SIZE + TILE_SIZE / 2;
    const wy = d.row * TILE_SIZE + TILE_SIZE / 2;
    const dx = wx - robot.x;
    const dy = wy - robot.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestX = wx;
      bestY = wy;
      found = true;
    }
  }
  return found ? { x: bestX, y: bestY } : null;
}

// Find nearest ally that has a RECHARGE_MODULE (for SEEK_RECHARGER action)
export function findNearestRecharger(robot: Robot, allies: Robot[]): Robot | null {
  let best: Robot | null = null;
  let bestDist = Infinity;
  for (const a of allies) {
    if (!a.alive || a.id === robot.id) continue;
    // Ally must have a recharge module
    if (!a.getSupportModules().some(m => m.def.rechargeRate > 0)) continue;
    // Ally must have energy to share
    if (a.currentEnergy < a.maxEnergy * 0.1) continue;
    const d = robot.distanceTo(a.x, a.y);
    if (d < bestDist) { bestDist = d; best = a; }
  }
  return best;
}

// Find nearest ally with low energy (for ALLY_LOW_ENERGY condition on recharger robots)
// Idle allies are preferred; combat allies only targetable by Lv3 rechargers
export function findAllyLowEnergy(robot: Robot, allies: Robot[]): Robot | null {
  const hasLv3 = robot.getSupportModules().some(
    m => m.def.rechargeRate > 0 && m.evoLevel >= 3,
  );
  let best: Robot | null = null;
  let bestDist = Infinity;
  for (const a of allies) {
    if (!a.alive || a.id === robot.id) continue;
    if (a.currentEnergy >= a.maxEnergy * 0.3) continue; // not low
    // Only idle allies can be recharged, unless Lv3
    if (!a.isIdle() && !hasLv3) continue;
    const d = robot.distanceTo(a.x, a.y);
    if (d < bestDist) { bestDist = d; best = a; }
  }
  return best;
}

// Diminishing returns for stacking support modules:
// module 1 = 100%, module 2 = 50%, module 3 = 25%, ...
// e.g. 2 recharge modules at 10/s each → 10 + 5 = 15/s (not 20)
export function diminishingFactor(index: number): number {
  return 1 / Math.pow(2, index);
}

export function repairBase(robot: Robot, base: PlayerBase, dt: number): boolean {
  const modules = robot.getSupportModules().filter(m => m.def.repairRate > 0);
  let idx = 0;
  for (const m of modules) {
    if (robot.distanceTo(base.x, base.y) > m.def.supportRange) continue;
    if (!robot.consumeEnergy(Math.max(0, -m.def.energy) * dt)) continue;
    const rate = m.def.repairRate * diminishingFactor(idx);
    base.heal(rate * dt, robot.x, robot.y);
    idx++;
  }
  return idx > 0;
}

export function repairAlly(robot: Robot, ally: Robot, dt: number): boolean {
  // Target must be idle (not in combat/moving) to receive repairs
  if (!ally.isIdle()) return false;
  const modules = robot.getSupportModules().filter(m => m.def.repairRate > 0);
  let idx = 0;
  for (const m of modules) {
    if (robot.distanceTo(ally.x, ally.y) > m.def.supportRange) continue;
    if (!robot.consumeEnergy(Math.max(0, -m.def.energy) * dt)) continue;
    const rate = m.def.repairRate * diminishingFactor(idx);
    ally.currentHp = Math.min(ally.totalHp, ally.currentHp + rate * dt);
    idx++;
  }
  if (idx > 0) ally.updateDebugOverlays();
  return idx > 0;
}

export function rechargeAlly(robot: Robot, ally: Robot, dt: number): boolean {
  // Target must be idle to receive recharge — UNLESS supporter has Lv3 recharge module
  const hasLv3 = robot.getSupportModules().some(
    m => m.def.rechargeRate > 0 && m.evoLevel >= 3,
  );
  const allyInCombat = !ally.isIdle();
  if (allyInCombat && !hasLv3) return false;
  // Lv3 can recharge in combat but at 50% efficiency
  const combatPenalty = allyInCombat ? 0.5 : 1;

  const modules = robot.getSupportModules().filter(m => m.def.rechargeRate > 0);
  let idx = 0;
  for (const m of modules) {
    if (robot.distanceTo(ally.x, ally.y) > m.def.supportRange) continue;
    // Recharge modules are free — no energy cost
    const rate = m.def.rechargeRate * diminishingFactor(idx) * combatPenalty;
    ally.currentEnergy = Math.min(ally.maxEnergy, ally.currentEnergy + rate * dt);
    idx++;
  }
  if (idx > 0) ally.updateDebugOverlays();
  return idx > 0;
}

export function spawnSupportBeam(
  scene: Phaser.Scene,
  fromX: number, fromY: number,
  toX: number, toY: number,
  type: 'repair' | 'recharge',
): void {
  const key = `${Math.round(fromX)}_${Math.round(fromY)}_${Math.round(toX)}_${Math.round(toY)}`;
  const now = scene.time.now;
  if (beamThrottle.has(key) && now - beamThrottle.get(key)! < 200) return;
  beamThrottle.set(key, now);

  // Periodic beamThrottle cleanup — purge entries older than 5s every ~300 calls
  if (++beamThrottleCleanupCounter >= 300) {
    beamThrottleCleanupCounter = 0;
    const cutoff = now - 5000;
    for (const [k, ts] of beamThrottle) {
      if (ts < cutoff) beamThrottle.delete(k);
    }
  }

  const isRepair = type === 'repair';
  const beamColor = isRepair ? 0x44ff66 : 0xffdd44;
  const particleColor = isRepair ? 0x44ff66 : 0xffee44;

  // ── Sinusoidal beam ──
  const gfx = scene.add.graphics().setDepth(27).setAlpha(0.35);
  const dx = toX - fromX;
  const dy = toY - fromY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 2) return;
  const steps = Math.max(12, Math.round(dist / 3));
  const amplitude = 3 + Math.random() * 2;
  const phase = Math.random() * Math.PI * 2;
  const nx = -dy / dist; // perpendicular normal
  const ny = dx / dist;

  gfx.lineStyle(2, beamColor, 0.6);
  gfx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const baseX = fromX + dx * t;
    const baseY = fromY + dy * t;
    const wave = Math.sin(phase + t * Math.PI * 4) * amplitude * (1 - Math.abs(t - 0.5) * 1.2);
    const px = baseX + nx * wave;
    const py = baseY + ny * wave;
    if (i === 0) gfx.moveTo(px, py);
    else gfx.lineTo(px, py);
  }
  gfx.strokePath();

  // Fade out beam
  scene.tweens.add({
    targets: gfx, alpha: 0, duration: 250,
    onComplete: () => gfx.destroy(),
  });

  // ── Particles at target: green crosses (repair) or yellow lightning (recharge) ──
  const particleCount = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < particleCount; i++) {
    const px = toX + (Math.random() - 0.5) * 12;
    const py = toY + (Math.random() - 0.5) * 12;

    if (isRepair) {
      // Green cross (+) particle — drawn in local coords, positioned via x/y
      const cross = scene.add.graphics().setPosition(px, py).setDepth(28);
      const size = 3 + Math.random() * 2;
      cross.lineStyle(1.5, particleColor, 0.9);
      cross.lineBetween(-size, 0, size, 0);
      cross.lineBetween(0, -size, 0, size);
      scene.tweens.add({
        targets: cross, alpha: 0, y: py - 8 - Math.random() * 6,
        duration: 400 + Math.random() * 200,
        onComplete: () => cross.destroy(),
      });
    } else {
      // Yellow lightning bolt particle (small zigzag)
      const bolt = scene.add.graphics().setDepth(28);
      bolt.lineStyle(1.5, particleColor, 0.9);
      const bx = px;
      const by = py;
      bolt.beginPath();
      bolt.moveTo(bx, by);
      bolt.lineTo(bx + 2, by - 3);
      bolt.lineTo(bx - 1, by - 4);
      bolt.lineTo(bx + 1, by - 7);
      bolt.strokePath();
      scene.tweens.add({
        targets: bolt, alpha: 0, y: by - 6 - Math.random() * 4,
        duration: 350 + Math.random() * 200,
        onComplete: () => bolt.destroy(),
      });
    }
  }
}

// ── Passive support tick ──
// Runs every frame BEFORE behavior rules. Handles:
//  1. Self-recharge (recharge modules regen own energy)
//  2. Self-repair (repair modules heal own HP)
//  3. Aura: auto-repair & auto-recharge nearby allies within supportRange
// No behavior rules required — support modules work as a passive aura.

export function passiveSupportTick(robot: Robot, allies: Robot[], delta: number, scene?: Phaser.Scene): void {
  const dt = delta / 1000;

  // ── Base passive recharge (all robots with a battery) ──
  // Very slow trickle so batteries aren't a death trap without RECHARGE_MODULE.
  // Rate: 1% of maxEnergy per second + 2/s per extra battery beyond the first.
  // Multiple batteries now meaningfully extend combat endurance.
  const BASE_RECHARGE_RATE = GOD.robotPassiveRechargeRate;
  const supportModules = robot.getSupportModules();
  const hasRechargeModule = supportModules.some(m => m.def.rechargeRate > 0 && !m.broken);

  if (!hasRechargeModule && robot.maxEnergy > 0 && robot.currentEnergy < robot.maxEnergy) {
    const batteryCount = robot.components.filter(c => !c.broken && c.def.type === ComponentType.BATTERY).length;
    const extraBatteryBonus = Math.max(0, batteryCount - 1) * 2; // +2 energy/s per extra battery
    robot.currentEnergy = Math.min(
      robot.maxEnergy,
      robot.currentEnergy + (robot.maxEnergy * BASE_RECHARGE_RATE + extraBatteryBonus) * dt,
    );
  }

  if (supportModules.length === 0) return;

  // ── Self-recharge ──
  // Recharge modules are free generators — they produce energy without consuming any.
  const rechargeModules = supportModules.filter(m => m.def.rechargeRate > 0);
  if (rechargeModules.length > 0 && robot.currentEnergy < robot.maxEnergy) {
    let idx = 0;
    for (const m of rechargeModules) {
      if (m.broken) continue;
      const rate = m.def.rechargeRate * diminishingFactor(idx);
      robot.currentEnergy = Math.min(robot.maxEnergy, robot.currentEnergy + rate * dt);
      idx++;
    }
  }

  // ── Self-repair ──
  // Repair modules consume energy to heal — unlike recharge, they need energy to operate.
  // But if the robot also has a recharge module, self-recharge above will have restored some energy.
  const repairModules = supportModules.filter(m => m.def.repairRate > 0);
  if (repairModules.length > 0 && robot.currentHp < robot.totalHp) {
    let idx = 0;
    for (const m of repairModules) {
      if (m.broken) continue;
      if (!robot.consumeEnergy(Math.max(0, -m.def.energy) * dt, true)) continue;
      const rate = m.def.repairRate * diminishingFactor(idx) * 0.5; // self-repair at 50% efficiency
      robot.healHp(rate * dt);
      idx++;
    }
  }

  // ── Camouflage energy drain ──
  // Camo modules consume energy passively — if no energy, Robot.updateCamouflage will decloak
  const camoModules = supportModules.filter(m => m.def.type === ComponentType.CAMOUFLAGE);
  for (const m of camoModules) {
    if (m.broken) continue;
    robot.consumeEnergy(Math.max(0, -m.def.energy) * dt, true);
  }

  // ── Aura: repair & recharge nearby allies ──
  // Recharge target limit by evolution level: Lv0 → 1 ally, Lv1 → 1, Lv2 → 2, Lv3 → unlimited
  const rechargeLevel = rechargeModules.length > 0
    ? Math.max(...rechargeModules.map(m => m.evoLevel))
    : 0;
  const hasLv3Recharge = rechargeLevel >= 3;
  const maxRechargeTargets = rechargeLevel >= 3 ? Infinity : Math.max(1, rechargeLevel);

  // Collect eligible allies sorted by distance (closest first)
  const eligibleAllies = allies
    .filter(a => a.alive && a.id !== robot.id)
    .map(a => ({ ally: a, dist: robot.distanceTo(a.x, a.y) }))
    .sort((a, b) => a.dist - b.dist);

  let rechargeTargetCount = 0;
  for (const { ally, dist } of eligibleAllies) {
    // Aura recharge (respects target limit)
    if (rechargeModules.length > 0 && rechargeTargetCount < maxRechargeTargets
        && ally.currentEnergy < ally.maxEnergy) {
      const allyInCombat = !ally.isIdle();
      if (!allyInCombat || hasLv3Recharge) {
        const combatPenalty = allyInCombat ? 0.5 : 1;
        let idx = 0;
        let didRecharge = false;
        for (const m of rechargeModules) {
          if (dist > m.def.supportRange) continue;
          // Recharge modules are free — no energy cost to recharge allies
          const rate = m.def.rechargeRate * diminishingFactor(idx) * combatPenalty;
          ally.currentEnergy = Math.min(ally.maxEnergy, ally.currentEnergy + rate * dt);
          idx++;
          didRecharge = true;
        }
        if (didRecharge) {
          rechargeTargetCount++;
          if (scene) spawnSupportBeam(scene, robot.x, robot.y, ally.x, ally.y, 'recharge');
        }
      }
    }

    // Aura repair (only idle allies, no target limit)
    if (repairModules.length > 0 && ally.currentHp < ally.totalHp && ally.isIdle()) {
      let idx = 0;
      let didRepair = false;
      for (const m of repairModules) {
        if (dist > m.def.supportRange) continue;
        if (!robot.consumeEnergy(Math.max(0, -m.def.energy) * dt, true)) continue;
        const rate = m.def.repairRate * diminishingFactor(idx);
        ally.healHp(rate * dt);
        idx++;
        didRepair = true;
      }
      if (didRepair && scene) {
        spawnSupportBeam(scene, robot.x, robot.y, ally.x, ally.y, 'repair');
      }
    }
  }

}

export function drainSensorEnergy(robot: Robot, delta: number): void {
  const dt = delta / 1000;
  for (const comp of robot.components) {
    if (comp.def.category === ComponentCategory.SENSOR) {
      // Level 0 (base) sensors are passive — no energy drain
      const level = comp.evoLevel;
      if (level === 0) continue;
      robot.consumeEnergy(Math.max(0, -comp.def.energy) * dt);
    }
  }
}

/** Passive idle drain: weapons/support consume 10% energy even when not firing (standby power) */
export function drainActiveComponentsEnergy(robot: Robot, dt: number): void {
  const IDLE_DRAIN_FACTOR = 0.10;
  for (const comp of robot.components) {
    if (comp.broken) continue;
    if (comp.def.category === ComponentCategory.WEAPON ||
        comp.def.category === ComponentCategory.SUPPORT) {
      // Passive standby drain — mark as passive support to avoid 2× penalty on support robots
      robot.consumeEnergy(Math.max(0, -comp.def.energy) * dt * IDLE_DRAIN_FACTOR, true);
    }
  }
}
